import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ProductScheduleListItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { cadenceEvery } from './cadence-text';
import { ScheduleStatusBadge } from './schedule-status-badge';

/** Column model of the schedules table; `meta` ties columns to the list's sorts and filters. */
export function useScheduleColumns() {
  const { t } = useTranslation('schedules');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ProductScheduleListItem>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('columns.name'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to="/org/schedules/$scheduleId"
              params={{ scheduleId: row.original.id }}
              className="block truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <span className="text-muted-foreground block truncate text-xs">
              {row.original.stores.length === 0
                ? t('columns.noStores')
                : row.original.stores.map((s) => s.storeName).join(', ')}
            </span>
          </div>
        ),
        meta: { sortField: 'name', lockVisibility: true },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <ScheduleStatusBadge status={row.original.status} />
            {row.original.pausedReason === 'run_failed' || row.original.rulesTruncated ? (
              <AlertTriangle
                className="text-warning-foreground size-4"
                aria-label={t('columns.needsAttention')}
              />
            ) : null}
          </span>
        ),
        meta: { filterKey: 'status', tour: 'schedules.list.status' },
        size: 130,
      },
      {
        id: 'liveCount',
        accessorKey: 'liveCount',
        header: t('columns.live'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('cadence.live', { count: row.original.liveCount })}
          </span>
        ),
        meta: { align: 'end' },
        size: 110,
      },
      {
        id: 'cadence',
        header: t('columns.cadence'),
        cell: ({ row }) => (
          <span className="min-w-0">
            <span className="block truncate tabular-nums">{cadenceEvery(t, row.original)}</span>
            <span className="text-muted-foreground block truncate text-xs">
              {t('cadence.zone', { zone: row.original.timeZone })}
            </span>
          </span>
        ),
      },
      {
        id: 'members',
        header: t('columns.pool'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('columns.poolCount', { count: row.original.members.total })}
            {row.original.members.pinned > 0 ? (
              <span className="text-muted-foreground">
                {' · '}
                {t('columns.pinnedCount', { count: row.original.members.pinned })}
              </span>
            ) : null}
          </span>
        ),
        meta: { align: 'end' },
        size: 140,
      },
      {
        id: 'nextRunAt',
        accessorKey: 'nextRunAt',
        header: t('columns.nextRun'),
        cell: ({ row }) =>
          row.original.nextRunAt ? (
            <span title={format.utc(row.original.nextRunAt)}>
              {format.relative(row.original.nextRunAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: { sortField: 'nextRunAt' },
        size: 140,
      },
      {
        id: 'lastRunAt',
        accessorKey: 'lastRunAt',
        header: t('columns.lastRun'),
        cell: ({ row }) =>
          row.original.lastRunAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.lastRunAt)}>
              {format.relative(row.original.lastRunAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('columns.never')}</span>
          ),
        size: 140,
      },
    ],
    [format, t],
  );
}
